import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { AsyncSelect, type FormatOptionLabelMeta } from '@databases/atlaskit/Select';
import { PageTemplateIcon } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import {
  type TemplateReference,
  templateReferenceValidator
} from '@databases/types/confluence';
import {
  CUSTOM_TEMPLATE_EMPTY,
  CUSTOM_TEMPLATE_ENTRY_MACRO
} from '@databases/types/database';
import { getAllTemplates } from '@databases/utils/confluence/template';
import { selectCurrentSpaceKey } from '@databases/utils/redux/context/selectors';
import { asyncGeneratorToArray } from '@databases/utils/utils';
import React, { type ReactNode, useCallback, useMemo, useState, useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { type StylesConfig } from 'react-select';

import { getCompactSelectStyles } from './compact-select-styles.ts';

// Cannot memoize because defaultTemplates might change depending on feature
// flag value/overrides
const getTemplates = async (spaceKey: string) => {
  return [
    {
      options: (await asyncGeneratorToArray(getAllTemplates(spaceKey)))
        .filter((template) => {
          // For some reason every blueprint is returned twice, with different IDs and bodies. The only distinguisher
          // seems to be the originalTemplate.moduleKey property, which is ends in -index or -index-page for the
          // ones that we don't want, and in -page for the ones that we want.
          return (
            template.templateType !== 'blueprint' ||
            !template.originalTemplate.moduleKey.match(/-index(-page)?$/)
          );
        })
        .map((t) => templateReferenceValidator.parse(t))
    }
  ];
};

const selectStyles: StylesConfig<TemplateReference, false> = {
  group: () => ({ '& + &': { borderTop: `1px solid ${colors.N30}` } }),
  groupHeading: () => ({ display: 'none' })
};
const compactBaseStyles = getCompactSelectStyles<TemplateReference, false>();
const compactStyles: StylesConfig<TemplateReference, false> = {
  ...selectStyles,
  ...compactBaseStyles
};
const valueOptionStyles = xcss({
  flexShrink: 1,
  minWidth: '0',
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
});
const valueIconStyles = xcss({
  display: 'flex',
  flexShrink: 0
});
const valueNameStyles = xcss({
  display: 'block',
  flexShrink: 1,
  minWidth: '0',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});
const getOptionValue = (value: TemplateReference) => value.templateId;

const getOptionLabel = (value: TemplateReference) => value.name || '';

const formatOptionLabel = (
  value: TemplateReference,
  meta: FormatOptionLabelMeta<TemplateReference>
): ReactNode => {
  return (
    <Inline
      space="space.100"
      alignBlock={meta.context === 'value' ? 'center' : 'start'}
      xcss={meta.context === 'value' && valueOptionStyles}
    >
      {meta.context === 'value' ? (
        <>
          <Box as="span" xcss={valueIconStyles}>
            <PageTemplateIcon label="" />
          </Box>
          <Box as="span" xcss={valueNameStyles}>
            {value.name}
          </Box>
        </>
      ) : (
        <>
          <PageTemplateIcon label="" />
          <Text color="inherit">{value.name}</Text>
        </>
      )}
    </Inline>
  );
};

interface PageTemplatePickerProps {
  value: TemplateReference;
  onChange: (value: TemplateReference) => void;
  inputId?: string;
  isCompact?: boolean;
}

/**
 * A select box that shows all page templates in the given space.
 */
const PageTemplatePicker: React.FunctionComponent<PageTemplatePickerProps> = ({
  value,
  onChange,
  inputId = 'page-template-picker-input',
  isCompact
}) => {
  const { formatMessage } = useIntl();
  const spaceKey = defaultStoreUtils.useSelector(selectCurrentSpaceKey);

  const [templates, setTemplates] = useState<Array<TemplateReference>>([]);
  const [templatesIsLoading, setTemplatesIsLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      const templates = await getTemplates(spaceKey);
      const allTemplates = templates.flatMap((item) => item.options);
      setTemplates(allTemplates);
      setTemplatesIsLoading(false);
    };

    fetchTemplates();
  }, [formatMessage, spaceKey]);

  const loadOptions = useCallback(
    (inputValue: string) => {
      // v1 templates are legacy and fabric unsupported,
      // but we include it if user currently has legacy template for page link field to not affect their data
      const allowedTemplates = templates.filter(
        (template) =>
          template.editorVersion !== 'v1' || value.templateId === template.templateId
      );
      if (inputValue) {
        const inputValueTrimmed = inputValue.trim();
        const filteredTemplates = allowedTemplates.filter((template) =>
          template.name?.toLowerCase().includes(inputValueTrimmed.toLowerCase())
        );
        return Promise.resolve(filteredTemplates);
      }

      return Promise.resolve(allowedTemplates);
    },
    [templates, value]
  );

  const handleChange = useCallback(
    (val: TemplateReference | null) => {
      const defaultValue = CUSTOM_TEMPLATE_EMPTY;
      onChange(val ?? defaultValue);
    },
    [onChange]
  );

  const resolvedValue = useMemo(() => {
    if (
      value.templateId === CUSTOM_TEMPLATE_EMPTY.templateId ||
      value.templateId === CUSTOM_TEMPLATE_ENTRY_MACRO.templateId
    ) {
      return null;
    } else {
      return value ?? null;
    }
  }, [value]);

  return (
    <AsyncSelect<TemplateReference>
      classNamePrefix="page-template-picker"
      styles={isCompact ? compactStyles : selectStyles}
      value={resolvedValue}
      onChange={handleChange}
      isClearable
      formatOptionLabel={formatOptionLabel}
      loadOptions={loadOptions}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      fetchValueFromOptions
      placeholder={formatMessage(
        messages['field.settings.page-link.page-template-placeholder']
      )}
      isLoading={templatesIsLoading}
      isOptionsLoading={templatesIsLoading}
      inputId={inputId}
      spacing={isCompact ? 'compact' : undefined}
    />
  );
};

export default PageTemplatePicker;
